import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
// @flow

/*
 * Licensed Materials - Property of IBM
 * 5724-Q36
 * (c) Copyright IBM Corp. 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Checkbox } from 'carbon-components-react';
import update from 'immutability-helper';
import { pkg } from '../../../../../settings';
import DraggableElement from '../../DraggableElement';
import { isColumnVisible } from './common';
import classNames from 'classnames';
var blockClass = "".concat(pkg.prefix, "--datagrid");

var getNextIndex = function getNextIndex(array, currentIndex, key) {
  var newIndex = -1;

  if (key === 'ArrowUp') {
    newIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : array.length - 1;
  }

  if (key === 'ArrowDown') {
    newIndex = currentIndex + 1 < array.length ? currentIndex + 1 : 0;
  }

  return newIndex;
};

var Columns = function Columns(_ref) {
  var _classNames;

  var getVisibleColumnsCount = _ref.getVisibleColumnsCount,
      filterString = _ref.filterString,
      columns = _ref.columns,
      setColumnsObject = _ref.setColumnsObject,
      onSelectColumn = _ref.onSelectColumn,
      assistiveTextInstructionsLabel = _ref.assistiveTextInstructionsLabel,
      assistiveTextDisabledInstructionsLabel = _ref.assistiveTextDisabledInstructionsLabel,
      selectAllLabel = _ref.selectAllLabel;

  var _React$useState = React.useState(''),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      ariaRegionText = _React$useState2[0],
      setAriaRegionText = _React$useState2[1];

  var _React$useState3 = React.useState(-1),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      focusIndex = _React$useState4[0],
      setFocusIndex = _React$useState4[1];

  var moveElement = React.useCallback(function (dragIndex, hoverIndex) {
    var dragCard = columns[dragIndex];
    setColumnsObject(update(columns, {
      $splice: [[dragIndex, 1], [hoverIndex, 0, dragCard]]
    }));
  }, [columns, setColumnsObject]);
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(blockClass, "__customize-columns-column-list")
  }, /*#__PURE__*/React.createElement(DndProvider, {
    backend: HTML5Backend
  }, /*#__PURE__*/React.createElement("ol", {
    className: "".concat(blockClass, "__customize-columns-column-list--focus"),
    role: "listbox",
    "aria-describedby": "".concat(blockClass, "__customize-columns--instructions"),
    onKeyDown: function onKeyDown(e) {
      var nextIndex = getNextIndex(columns, focusIndex, e.key);

      if (nextIndex >= 0) {
        setFocusIndex(nextIndex);
        e.preventDefault();
        e.stopPropagation();
      }
    },
    tabIndex: 0,
    onFocus: function onFocus(e) {
      if (e.target === e.currentTarget) {
        setFocusIndex(0);
      }
    }
  }, /*#__PURE__*/React.createElement("span", {
    "aria-live": "assertive",
    className: "".concat(blockClass, "__shared-ui--assistive-text")
  }, ariaRegionText), /*#__PURE__*/React.createElement("span", {
    id: "".concat(blockClass, "__customize-columns--instructions"),
    className: "".concat(blockClass, "__shared-ui--assistive-text")
  }, filterString.length === 0 ? assistiveTextInstructionsLabel : assistiveTextDisabledInstructionsLabel), /*#__PURE__*/React.createElement("div", {
    id: "".concat(blockClass, "__customize-columns-select-all"),
    className: classNames((_classNames = {}, _defineProperty(_classNames, "".concat(blockClass, "__customize-columns-select-all"), getVisibleColumnsCount() === 0), _defineProperty(_classNames, "".concat(blockClass, "__customize-columns-select-all-selected"), getVisibleColumnsCount() > 0), _classNames)),
    selected: getVisibleColumnsCount() > 0
  }, /*#__PURE__*/React.createElement(Checkbox, {
    wrapperClassName: "".concat(blockClass, "__customize-columns-checkbox-wrapper"),
    checked: getVisibleColumnsCount() === columns.length,
    indeterminate: getVisibleColumnsCount() < columns.length && getVisibleColumnsCount() > 0,
    onChange: function onChange() {
      onSelectColumn(columns, getVisibleColumnsCount() !== columns.length);
    },
    id: "".concat(blockClass, "__customization-column-select-all"),
    labelText: selectAllLabel
  })), columns.filter(function (colDef) {
    return filterString.length === 0 || colDef.Header.props.title.toLowerCase().includes(filterString);
  }).map(function (colDef, i) {
    return /*#__PURE__*/React.createElement(DraggableElement, {
      key: colDef.id,
      index: i,
      listData: columns,
      setListData: setColumnsObject,
      id: "dnd-datagrid-columns-".concat(colDef.id),
      type: "column-customization",
      disabled: filterString.length > 0,
      ariaLabel: colDef.Header.props.title,
      onGrab: setAriaRegionText,
      isFocused: focusIndex === i,
      moveElement: moveElement,
      onArrowKeyDown: function onArrowKeyDown(e, isGrabbed, currentIndex) {
        if (isGrabbed) {
          var nextIndex = getNextIndex(columns, currentIndex, e.key);
          e.preventDefault();
          e.stopPropagation();

          if (nextIndex >= 0) {
            setFocusIndex(nextIndex);
            moveElement(currentIndex, nextIndex);
            e.target.scrollIntoView({
              block: 'center'
            });
          }
        }
      },
      selected: isColumnVisible(colDef)
    }, /*#__PURE__*/React.createElement(Checkbox, {
      wrapperClassName: "".concat(blockClass, "__customize-columns-checkbox-wrapper"),
      checked: isColumnVisible(colDef),
      onChange: onSelectColumn.bind(null, colDef),
      id: "".concat(blockClass, "__customization-column-").concat(colDef.id),
      labelText: colDef.Header.props.title,
      title: colDef.Header.props.title,
      className: "".concat(blockClass, "__customize-columns-checkbox")
    }));
  }))));
};

Columns.propTypes = {
  assistiveTextDisabledInstructionsLabel: PropTypes.string,
  assistiveTextInstructionsLabel: PropTypes.string,
  columns: PropTypes.array.isRequired,
  disabledInstructionsLabel: PropTypes.string,
  filterString: PropTypes.string.isRequired,
  getVisibleColumnsCount: PropTypes.func.isRequired,
  instructionsLabel: PropTypes.string,
  onSelectColumn: PropTypes.func.isRequired,
  selectAllLabel: PropTypes.string,
  setColumnStatus: PropTypes.func,
  setColumnsObject: PropTypes.func.isRequired
};
export default Columns;